import { useState } from "react";
import { format } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/format";

/** Which follow-up stage this lead is sitting in. */
const StagePicker = ({ lead, stages, busy, onMove }) => (
  <div>
    <Label className="text-[13px] font-semibold">Follow-up stage</Label>
    <Select value={lead.status} onValueChange={onMove} disabled={busy === "status"}>
      <SelectTrigger className="mt-1.5 h-10" data-testid="lead-sheet-status-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {stages.map((stage) => (
          <SelectItem
            key={stage.key}
            value={stage.key}
            data-testid={`lead-sheet-status-${stage.key}`}
          >
            {stage.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/** What the date button reads before and after a date is set. */
const DateLabel = ({ lead }) => {
  if (!lead.next_action_date) return <span className="text-muted-foreground">Pick a date</span>;
  return (
    <span className={lead.next_action_overdue ? "font-semibold text-destructive" : ""}>
      {formatDate(lead.next_action_date)}
      {lead.next_action_overdue ? " · overdue" : ""}
    </span>
  );
};

/** When somebody has promised to do the next thing. */
const NextActionDate = ({ lead, patch }) => {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <Label className="text-[13px] font-semibold">Next action date</Label>
      <div className="mt-1.5 flex items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              data-testid="lead-sheet-next-action-button"
              className="h-10 flex-1 justify-start bg-white font-normal"
            >
              <CalendarDays className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
              <DateLabel lead={lead} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto bg-white p-0" align="start">
            <Calendar
              mode="single"
              selected={lead.next_action_date ? new Date(lead.next_action_date) : undefined}
              onSelect={(value) => {
                setOpen(false);
                if (value) patch({ next_action_date: format(value, "yyyy-MM-dd") }, "date");
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {lead.next_action_date ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Clear the next action date"
            data-testid="lead-sheet-clear-next-action"
            onClick={() => patch({ clear_next_action: true }, "date")}
            className="h-10 w-10 shrink-0"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const NO_REASON = "__none__";

/** Why a closed lead closed the way it did. Always optional. */
const OutcomeReasonPicker = ({ lead, stageReasons, busy, patch }) => (
  <div>
    <Label className="text-[13px] font-semibold">
      Why we {lead.status === "won" ? "won" : "lost"} it
    </Label>
    <Select
      value={lead.outcome_reason || NO_REASON}
      onValueChange={(value) =>
        patch({ outcome_reason: value === NO_REASON ? "" : value }, "reason")
      }
      disabled={busy === "reason"}
    >
      <SelectTrigger className="mt-1.5 h-10" data-testid="lead-sheet-reason-select">
        <SelectValue placeholder="Not recorded" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_REASON} data-testid="lead-sheet-reason-none">
          Not recorded
        </SelectItem>
        {stageReasons.map((reason) => (
          <SelectItem
            key={reason.key}
            value={reason.key}
            data-testid={`lead-sheet-reason-${reason.key}`}
          >
            {reason.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {lead.needs_outcome_reason ? (
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Optional — but it's what makes the closing breakdown worth reading.
      </p>
    ) : null}
  </div>
);

/**
 * Stage, next action date, and - once the lead is closed - the reason.
 *
 * Moving a lead to Won or Lost is the one moment the team actually knows why,
 * so the move itself raises the reason prompt rather than waiting to be asked.
 */
export const StageBlock = ({ lead, stages, reasons, busy, patch, onOutcomeNeeded }) => {
  const stageReasons = reasons?.[lead.status] || [];

  const moveTo = async (value) => {
    const updated = await patch({ status: value }, "status");
    if (!updated?.needs_outcome_reason || !(reasons?.[value] || []).length) return;
    onOutcomeNeeded?.({
      lead: updated,
      stage: value,
      stageLabel: updated.status_label,
      reasons: reasons[value],
    });
  };

  return (
    <div className="mt-5 space-y-4 rounded-lg border border-border p-4">
      <StagePicker lead={lead} stages={stages} busy={busy} onMove={moveTo} />
      <NextActionDate lead={lead} patch={patch} />
      {!lead.is_open && stageReasons.length ? (
        <OutcomeReasonPicker
          lead={lead}
          stageReasons={stageReasons}
          busy={busy}
          patch={patch}
        />
      ) : null}
    </div>
  );
};
